import { type RefObject, useEffect } from 'react';
import { appContext } from '../../app/context';
import { getLayer, updateLayer } from '../../core/model/document';
import { isGroup } from '../../core/model/tree';
import type { Rect, Vec2 } from '../../core/model/types';
import {
  angleFromCentre,
  boundsCenter,
  groupFootprint,
  hitTestLayer,
  hitTestStack,
  hitTestTransformHandle,
  isMovable,
  isNegligibleTransform,
  jointMembers,
  type LayerTransform,
  layerBounds,
  type MoveResult,
  moveForDrag,
  type RotateResult,
  rotateForDrag,
  rotateSetForDrag,
  type ScaleResult,
  type SetMember,
  type SetScaleResult,
  scaleForDrag,
  scaleSetForDrag,
  unionBounds,
} from '../../core/move';
import { defaultSnapTargets, type HandleId, isInsideRect } from '../../core/selection';
import { type LayerPreview, useEditorStore } from '../../state/editorStore';
import { clientToDoc } from '../pointer';

/**
 * The move tool: click a layer to select it, drag to move it, drag a handle
 * to scale it, drag the knob above the box to rotate it.
 *
 * Hit-testing is model-based, not engine-based: the scene objects are
 * `evented: false` on purpose, and the same pure functions the tests cover
 * (`hitTestStack`, `moveForDrag`, `scaleForDrag`, `rotateForDrag`) decide
 * what a click means. A drag lives in previews until release — one gesture,
 * one `commit`, or none at all if the pointer never really moved.
 *
 * Over a multi-selection (Iteration 12): a drag on any selected member moves
 * every unlocked member by the same delta, snapping runs on the union bounds
 * of the members, and the whole gesture is one history entry. Shift-click
 * toggles a layer into the set; dragging a selected member moves the set
 * without re-selecting.
 *
 * Joint scale and rotate (Iteration 15): the handles and the knob move to
 * the union box of the selection, and the pure set maths
 * (`scaleSetForDrag`, `rotateSetForDrag`) maps every member — corners scale
 * uniformly, edges appear only when every member is unrotated, the knob
 * orbits every member centre. A group selected in the panel is a selection:
 * its visible, unlocked leaves are the members, the box is their union, and
 * a drag inside it moves the group. The group's own transform fields stay
 * neutral — a group is its members, not a transformed object.
 */

const CORNER_HANDLES: readonly HandleId[] = ['nw', 'ne', 'se', 'sw'];

type Session =
  | {
      kind: 'move';
      label: string;
      /** The layer whose handles and outline the overlay draws — a member, or the group itself. */
      primaryId: string;
      /** Every layer the drag moves — one, the whole selection, or a group's leaves. */
      members: SetMember[];
      /** Union bounds of the members: what snapping runs on. */
      bounds: Rect;
      origin: Vec2;
      last: MoveResult;
    }
  | {
      kind: 'scale';
      label: string;
      primaryId: string;
      layerId: string;
      start: LayerTransform;
      bounds: Rect;
      origin: Vec2;
      handle: HandleId;
      last: ScaleResult;
    }
  | {
      kind: 'rotate';
      label: string;
      primaryId: string;
      layerId: string;
      start: LayerTransform;
      bounds: Rect;
      origin: Vec2;
      grabAngle: number;
      last: RotateResult;
    }
  | {
      kind: 'jointScale';
      label: string;
      primaryId: string;
      members: SetMember[];
      bounds: Rect;
      origin: Vec2;
      handle: HandleId;
      last: SetScaleResult | null;
    }
  | {
      kind: 'jointRotate';
      label: string;
      primaryId: string;
      members: SetMember[];
      bounds: Rect;
      origin: Vec2;
      grabAngle: number;
      last: ReturnType<typeof rotateSetForDrag> | null;
    };

/** Screen-pixel grab sizes converted to document space at the current zoom. */
const grab = (zoom: number) => ({ tolerance: 8 / zoom, rotateOffset: 24 / zoom });

/**
 * Which handles a joint box offers: corners always, edges only when every
 * member is unrotated (a non-uniform stretch over a rotated member is a skew
 * the model cannot express), the rotate knob always.
 */
function edgesAllowed(members: SetMember[]): boolean {
  return members.every((member) => member.start.angle % 360 === 0);
}

/**
 * What a pointer-down on a joint box means: a scale handle, the rotate knob,
 * a move of the whole set, or nothing. The box is never rotated, so the
 * hit-test runs in plain document space at angle 0.
 */
function jointHandleAt(
  members: SetMember[],
  bounds: Rect,
  point: Vec2,
  zoom: number,
): HandleId | 'rotate' | 'move' | null {
  const handle = hitTestTransformHandle(bounds, 0, point, grab(zoom));
  if (handle === 'rotate') return 'rotate';
  if (handle) {
    if (handle.length === 2 || edgesAllowed(members)) return handle;
    return null;
  }
  return isInsideRect(bounds, point) ? 'move' : null;
}

/** The history label: a group is one thing, a set is its count. */
function jointLabel(
  doc: Parameters<typeof getLayer>[0],
  primaryId: string,
  verb: string,
  count: number,
): string {
  const primary = getLayer(doc, primaryId);
  if (primary && isGroup(primary)) return `${verb} Layer`;
  return count > 1 ? `${verb} ${count} Layers` : `${verb} Layer`;
}

export function useMoveTool(stageRef: RefObject<HTMLDivElement | null>): void {
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    let session: Session | null = null;

    const endDrag = (cancelled: boolean) => {
      const drag = session;
      session = null;
      if (!drag) return;
      const store = useEditorStore.getState();
      store.setLayerPreview(null);
      store.setLayerPreviews(null);
      const engine = appContext.getEngine();
      if (drag.kind === 'move' || drag.kind === 'jointScale' || drag.kind === 'jointRotate') {
        for (const member of drag.members) engine?.setLayerPreview(member.id, null);
      } else {
        engine?.setLayerPreview(drag.layerId, null);
      }
      if (cancelled) return;

      if (drag.kind === 'move') {
        // Where the union box landed decides the delta every member shares —
        // snapping is computed once for the set, not once per layer.
        const dx = drag.last.x - drag.bounds.x;
        const dy = drag.last.y - drag.bounds.y;
        if (Math.abs(dx) < 1e-6 && Math.abs(dy) < 1e-6) return;
        let doc = store.doc;
        for (const member of drag.members) {
          doc = updateLayer(doc, member.id, {
            x: member.start.x + dx,
            y: member.start.y + dy,
          });
        }
        if (doc === store.doc) return;
        store.commit(drag.label, doc);
        return;
      }

      if (drag.kind === 'jointScale' || drag.kind === 'jointRotate') {
        const result = drag.last;
        if (!result) return;
        let doc = store.doc;
        let moved = false;
        drag.members.forEach((member, index) => {
          const after = result.transforms[index];
          if (!after || isNegligibleTransform(member.start, after)) return;
          moved = true;
          doc = updateLayer(doc, member.id, after);
        });
        if (!moved || doc === store.doc) return;
        store.commit(drag.label, doc);
        return;
      }

      const after = transformAfter(drag);
      if (isNegligibleTransform(drag.start, after)) return;
      store.commit(
        drag.label,
        updateLayer(store.doc, drag.layerId, {
          x: after.x,
          y: after.y,
          scaleX: after.scaleX,
          scaleY: after.scaleY,
          angle: after.angle,
        }),
      );
    };

    const showPreview = (drag: Session) => {
      const engine = appContext.getEngine();
      const store = useEditorStore.getState();
      if (drag.kind === 'move') {
        const dx = drag.last.x - drag.bounds.x;
        const dy = drag.last.y - drag.bounds.y;
        const previews: LayerPreview[] = [];
        for (const member of drag.members) {
          const transform = { ...member.start, x: member.start.x + dx, y: member.start.y + dy };
          engine?.setLayerPreview(member.id, transform);
          previews.push({ layerId: member.id, ...transform });
        }
        store.setLayerPreviews(previews);
        const primary = previews.find((one) => one.layerId === drag.primaryId);
        if (primary) store.setLayerPreview(primary);
        return;
      }
      if (drag.kind === 'jointScale' || drag.kind === 'jointRotate') {
        const result = drag.last;
        if (!result) return;
        const previews: LayerPreview[] = [];
        drag.members.forEach((member, index) => {
          const transform = result.transforms[index];
          if (!transform) return;
          engine?.setLayerPreview(member.id, transform);
          previews.push({ layerId: member.id, ...transform });
        });
        store.setLayerPreviews(previews);
        const primary = previews.find((one) => one.layerId === drag.primaryId);
        if (primary) store.setLayerPreview(primary);
        return;
      }
      const transform = transformAfter(drag);
      store.setLayerPreview({ layerId: drag.primaryId, ...transform });
      engine?.setLayerPreview(drag.layerId, transform);
    };

    const onPointerDown = (event: PointerEvent) => {
      // A pan gesture (space or middle button) is handled by the stage in
      // capture phase and calls preventDefault; honour that.
      if (event.defaultPrevented || event.button !== 0) return;
      const store = useEditorStore.getState();
      if (store.activeTool !== 'move') return;

      const point = clientToDoc(event.clientX, event.clientY, stage, store.viewport);
      const active = store.activeLayerId ? getLayer(store.doc, store.activeLayerId) : undefined;

      // A locked layer only gets grabbed if the Layers panel selected it —
      // hitTestStack click-throughs locked layers for everyone else. A locked
      // group is a locked box: its footprint is the union of its visible
      // leaves. Either way, refuse out loud rather than silently move what is
      // underneath.
      if (active?.locked) {
        const footprint = isGroup(active)
          ? groupFootprint(store.doc, active.id)
          : isMovable(active)
            ? layerBounds(active)
            : null;
        if (footprint && isInsideRect(footprint, point)) {
          store.notify(`“${active.name}” is locked`, 'warn');
          event.preventDefault();
          return;
        }
      }

      // Handles belong to the selection's box and win over a move drag, so
      // grabbing a corner never nudges the layer sideways first. Over a group
      // — or a set of more than one movable layer — the box is the union of
      // the members (Iteration 15); a lone primary keeps its own rotated box.
      if (active && !active.locked && active.visible) {
        const jointIds = store.selectedIds;
        const jointCandidates = jointMembers(store.doc, jointIds);
        const singleCandidate = isMovable(active) ? layerBounds(active) : null;
        const wantsJoint =
          isGroup(active) || (jointCandidates.length > 1 && jointIds.has(active.id));
        if (wantsJoint && jointCandidates.length > 0) {
          const bounds = unionBounds(jointCandidates);
          if (bounds) {
            const target = jointHandleAt(jointCandidates, bounds, point, store.viewport.zoom);
            if (target === 'rotate') {
              session = {
                kind: 'jointRotate',
                label: jointLabel(store.doc, active.id, 'Rotate', jointCandidates.length),
                primaryId: active.id,
                members: jointCandidates,
                bounds,
                origin: point,
                grabAngle: angleFromCentre(boundsCenter(bounds), point),
                last: null,
              };
              event.preventDefault();
              return;
            }
            if (target && target !== 'move') {
              session = {
                kind: 'jointScale',
                label: jointLabel(store.doc, active.id, 'Scale', jointCandidates.length),
                primaryId: active.id,
                members: jointCandidates,
                bounds,
                origin: point,
                handle: target,
                last: null,
              };
              event.preventDefault();
              return;
            }
            if (target === 'move') {
              session = {
                kind: 'move',
                label: jointLabel(store.doc, active.id, 'Move', jointCandidates.length),
                primaryId: active.id,
                members: jointCandidates,
                bounds,
                origin: point,
                last: { x: bounds.x, y: bounds.y, snappedX: null, snappedY: null },
              };
              event.preventDefault();
              return;
            }
          }
        } else if (singleCandidate) {
          const bounds = singleCandidate;
          const handle = hitTestTransformHandle(
            bounds,
            active.angle,
            point,
            grab(store.viewport.zoom),
          );
          if (handle === 'rotate') {
            session = {
              kind: 'rotate',
              label: 'Rotate Layer',
              primaryId: active.id,
              layerId: active.id,
              start: transformOf(active),
              bounds,
              origin: point,
              grabAngle: angleFromCentre(boundsCenter(bounds), point),
              last: { angle: active.angle, snapped: false },
            };
            event.preventDefault();
            return;
          }
          if (handle) {
            session = {
              kind: 'scale',
              label: 'Scale Layer',
              primaryId: active.id,
              layerId: active.id,
              start: transformOf(active),
              bounds,
              origin: point,
              handle,
              last: { scaleX: active.scaleX, scaleY: active.scaleY, x: active.x, y: active.y },
            };
            event.preventDefault();
            return;
          }
        }
      }

      const hit = hitTestStack(store.doc, point);
      if (!hit) {
        store.setActiveLayer(null);
        return;
      }

      // Shift-click toggles the layer into (or out of) the set; a drag that
      // follows moves the set. A plain click on a selected member keeps the
      // set — grabbing one layer of a selection is how you move all of it.
      if (event.shiftKey) {
        store.toggleSelected(hit.id);
      } else if (!store.selectedIds.has(hit.id)) {
        store.setActiveLayer(hit.id);
      }

      const after = useEditorStore.getState();
      if (!after.selectedIds.has(hit.id)) return; // the toggle removed it
      const members = jointMembers(after.doc, after.selectedIds);
      const bounds = unionBounds(members);
      if (!bounds) return;

      session = {
        kind: 'move',
        label: jointLabel(after.doc, hit.id, 'Move', members.length),
        primaryId: hit.id,
        members,
        bounds,
        origin: point,
        last: { x: bounds.x, y: bounds.y, snappedX: null, snappedY: null },
      };
      event.preventDefault();
    };

    const onPointerMove = (event: PointerEvent) => {
      const store = useEditorStore.getState();
      const point = clientToDoc(event.clientX, event.clientY, stage, store.viewport);
      const drag = session;

      if (!drag) {
        updateHover(store, point);
        return;
      }
      if (store.activeTool !== 'move') return;
      const delta = { x: point.x - drag.origin.x, y: point.y - drag.origin.y };
      // A click that never really moved is a click, not a drag: no preview, no
      // commit — the same negligible-drag rule the shape tools use.
      if (Math.abs(delta.x) < 1 && Math.abs(delta.y) < 1) return;

      if (drag.kind === 'move') {
        const targets = defaultSnapTargets(store.doc, store.guides);
        drag.last = moveForDrag(drag.bounds, delta, targets, 6 / store.viewport.zoom);
      } else if (drag.kind === 'scale') {
        // Corners keep the layer's proportions by default; Shift unlocks a
        // free stretch — the inverse of the crop tool, because here you are
        // distorting a picture, not framing one.
        const aspectLock = CORNER_HANDLES.includes(drag.handle) && !event.shiftKey;
        const layer = getLayer(store.doc, drag.layerId);
        if (!layer) return endDrag(true);
        drag.last = scaleForDrag(layer, drag.bounds, drag.handle, delta, { aspectLock });
      } else if (drag.kind === 'rotate') {
        drag.last = rotateForDrag(
          boundsCenter(drag.bounds),
          point,
          drag.start.angle,
          drag.grabAngle,
          { snap: event.shiftKey },
        );
      } else if (drag.kind === 'jointScale') {
        // Same corner rule as a single layer, with the model's one refusal:
        // a stretch that could part fx from fy over a rotated member does not
        // exist, so Shift only unlocks when every member is unrotated.
        const allUnrotated = edgesAllowed(drag.members);
        const aspectLock =
          (CORNER_HANDLES.includes(drag.handle) || !allUnrotated) && !event.shiftKey;
        drag.last = scaleSetForDrag(drag.members, drag.bounds, drag.handle, delta, {
          aspectLock,
        });
      } else {
        drag.last = rotateSetForDrag(
          drag.members,
          boundsCenter(drag.bounds),
          point,
          drag.grabAngle,
          { snap: event.shiftKey },
        );
      }
      showPreview(drag);
    };

    const onPointerUp = () => endDrag(false);
    const onPointerCancel = () => endDrag(true);

    stage.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerCancel);

    return () => {
      stage.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
      endDrag(true);
      useEditorStore.getState().setMoveHover(null);
    };
  }, [stageRef]);
}

/** The cursor affordance: which handle (or the body) is under the pointer. */
function updateHover(store: ReturnType<typeof useEditorStore.getState>, point: Vec2): void {
  if (store.activeTool !== 'move') return;
  let hover: string | null = null;
  const active = store.activeLayerId ? getLayer(store.doc, store.activeLayerId) : undefined;
  if (active?.visible && !active.locked) {
    if (isGroup(active)) {
      const members = jointMembers(store.doc, store.selectedIds);
      const bounds = unionBounds(members);
      if (bounds) {
        const target = jointHandleAt(members, bounds, point, store.viewport.zoom);
        hover = target;
      }
    } else if (isMovable(active)) {
      const bounds = layerBounds(active);
      if (bounds) {
        hover =
          hitTestTransformHandle(bounds, active.angle, point, grab(store.viewport.zoom)) ??
          (hitTestLayer(active, point) ? 'move' : null);
      }
    }
  }
  if (hover !== store.moveHover) store.setMoveHover(hover);
}

function transformOf(layer: {
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  angle: number;
}): LayerTransform {
  return { x: layer.x, y: layer.y, scaleX: layer.scaleX, scaleY: layer.scaleY, angle: layer.angle };
}

function transformAfter(
  drag: Exclude<Session, { kind: 'move' } | { kind: 'jointScale' } | { kind: 'jointRotate' }>,
): LayerTransform {
  if (drag.kind === 'scale') {
    return {
      ...drag.start,
      x: drag.last.x,
      y: drag.last.y,
      scaleX: drag.last.scaleX,
      scaleY: drag.last.scaleY,
    };
  }
  return { ...drag.start, angle: drag.last.angle };
}
